'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { CurrentUserQuery } from 'generated/operations';
import { useCommunity } from 'contexts/communityApp';
import { useRouter } from 'next/navigation';
import NextImage from 'next/image';
import { OWNER_ROUTES } from 'utils/routes';
import { generalHostFromWindow } from 'app/_auth/subdomains';
import { useChooseCommunityTracking } from 'app/_tracking/events';


export type CommunityType = NonNullable<NonNullable<NonNullable<
  CurrentUserQuery['currentUser']>['communityMembers']>[number]['community']>;

interface CommunitySwitcherProps {
  community?: CommunityType;
  communities: CommunityType[];
}

const CommunitySwitcher: React.FC<CommunitySwitcherProps> = (props) => {
  const router = useRouter();
  const { chooseCommunity, community: chosenCommunity } = useCommunity();
  const { trackChosen, trackChosenAll, trackOpened, trackCreate } = useChooseCommunityTracking();

  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = () => {
    trackOpened();
    setIsOpen(!isOpen);
  };

  const formatMemberCount = (count?: number | null) => {
    if (!count) return '';
    if (count >= 1000) {
      return `${Math.floor(count / 1000)}K+ members`;
    }
    if (count == 1) return '1 member';
    if (count == 0) return '';
    return `${count} members`;
  };

  const handleSelect = useCallback((community: CommunityType) => {
    chooseCommunity(community);
    trackChosen();
    window.location.hostname = generalHostFromWindow(window);
    setIsOpen(false);
  }, [chooseCommunity, trackChosen]);

  const handleAllSpacesClick = () => {
    chooseCommunity(undefined);
    trackChosenAll();
    window.location.hostname = generalHostFromWindow(window);
    setIsOpen(false);
  };

  const handleCreateSpace = () => {
    trackCreate();
    const host = `${window.location.protocol}//${generalHostFromWindow(window)}`;
    router.push(`${host}${OWNER_ROUTES.START.getPath()}`);
    setIsOpen(false);
  };

  const currentCommunity = useMemo(() => {
    if (chosenCommunity) {
      return props.communities.find((c) => c.id === chosenCommunity?.id) || props.community;
    }
    return props.community
  }, [chosenCommunity, props.communities, props.community])

  return (
    <div className={`w-full max-w-md bg-white ${isOpen ? 'rounded-[32px]' : 'rounded-[48px]'} `}>
      <div
        className="flex items-center justify-between p-3"
        onClick={toggleOpen}
        aria-expanded={isOpen}
        role="combobox"
        aria-controls="options-list"
      >
        <div className='flex items-center space-x-3'>
          <>
            <Logo src={currentCommunity?.logo || undefined} alt={currentCommunity?.name || undefined} />
            <div className={'flex-1'}>
              <h2 className='text-gray-950 font-semibold text-small line-clamp-1 break-all'>
                {currentCommunity?.name ?? 'All Spaces'}
              </h2>
              <p className='text-gray-400 font-regular text-xs line-clamp-1'>
                {formatMemberCount(currentCommunity?.membersCount)}
              </p>
            </div>
          </>
        </div>
        <div className="text-gray-400">
          {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </div>
      </div>

      <div className={`flex-col gap-[1px] max-h-80 overflow-y-auto ${isOpen ? "flex" : "hidden"}`}>

        <hr className={"block border-t border-gray-200 mt-[6px] mb-[10px] mx-3"}/>

        <Option id={'all'} onSelect={handleAllSpacesClick} isChosen={!props.community}>
          <p className='text-gray-950 font-semibold text-medium line-clamp-1'>All spaces</p>
        </Option>

        {props.communities.map((c, index) => (
          <Option
            key={index}
            id={c.subdomain ?? `${index}`}
            onSelect={() => handleSelect(c)}
            isChosen={props.community?.subdomain === c.subdomain}
          >
            <div className='flex items-center space-x-3'>
              <Logo src={c?.logo ?? ''} alt={c?.name} />
              <div className={'flex-1'}>
                <p className="text-gray-950 font-semibold text-small line-clamp-1 break-all">{c?.name}</p>
                <p className="text-gray-400 font-regular text-xs leading-[18px] line-clamp-1">
                  {formatMemberCount(c?.membersCount)}
                </p>
              </div>
            </div>
          </Option>
        ))}

        <div
          className="py-[9px] h-[53px] px-3 flex items-center space-x-3 justify-between hover:bg-gray-50 cursor-pointer"
          onClick={handleCreateSpace}
        >
          <div className="w-[35px] h-[35px] rounded-full border border-gray-100 flex items-center justify-center">
            <Plus size={18} className="text-gray-700" />
          </div>
          <p className="flex-1 text-gray-950 font-semibold text-small">Create new space</p>
        </div>
      </div>
    </div>
  );
};

type OptionType = {
  id: string;
  onSelect: () => void;
  isChosen: boolean
}

const Option: React.FC<OptionType & { children?: React.ReactNode }> = (props) => {
  return (
    <label
      className={`py-[9px] h-[53px] px-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer ${props.isChosen ? 'bg-gray-50' : ''}`}
      onClick={() => props.onSelect()}
      htmlFor={props.id}
    >
      <input
        type='radio'
        className='hidden'
        id={props.id}
        checked={props.isChosen}
        onChange={() => props.onSelect()}
      />
      <div className={'flex-1'}>
        {props.children}
      </div>

      <div className='flex items-center'>
        <div className={`w-6 h-6 rounded-full border ${props.isChosen ? 'border-indigo-500 border-[8px]' : 'border-gray-200'}`}>
        </div>
      </div>
    </label>
  )
}

const Logo = ({ src, alt }: { src?: string; alt?: string }) => {
  return (
    <div
      className={`w-[32px] h-[32px] rounded-full overflow-hidden`}>
      {src ? (
        <NextImage src={src} width={32} height={32} alt={alt || 'Community Logo'} />
      ) : (
        <NextImage src={'/logo/default-community-logo.svg'} width={32} height={32} alt='Community Logo'
                   className='w-[32px] h-[32px]' />
      )}
    </div>
  )
}
export default CommunitySwitcher;